import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { DialogService, DynamicDialogRef } from '../../../core/services/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppLogoComponent } from '../../../../assets/logo/app-logo';
import { BreadcrumbService } from '../../../core/services/breadcrumb/breadcrumb.service';
import { LayoutService } from '../../../core/services/layout/layout.service';
import { RoleService } from '../../../core/services/role/role.service';
import { RtoAccessService } from '../../../core/services/rto/rto-access.service';
import { LocalStorageService } from '../../../core/services/local-storage/local-storage.service';
import { NavigationStateService } from '../../../core/services/navigation/navigation-state.service';
import { PinnedModulesService } from '../../../core/services/navigation/pinned-modules.service';
import { UtilitiesService } from '../../../core/services/utilities/utilities.service';
import { Breadcrumb } from '../../models/breadcrumb.model';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { BreadcrumbShipmentNavComponent } from '../header/breadcrumb-shipment-nav/breadcrumb-shipment-nav.component';
import { GlobalSearch } from '../header/global-search/global-search';
import { RecentDownloadsPopupComponent } from '../header/recent-downloads-popup/recent-downloads-popup.component';
import { IcNotificationComponent } from '../Icons/ic-notification/ic-notification';
import { IcSearchComponent } from '../Icons/ic-search/ic-search';
import { IcUserComponent } from '../Icons/ic-user/ic-user';
import { NotificationsAndAlertsComponent } from '../notifications-and-alerts/notifications-and-alerts.component';
import { UserProfilePopupComponent } from '../user-profile-popup/user-profile-popup.component';
import { MegaMenuComponent } from './mega-menu/mega-menu.component';
import { ModuleSwitcherComponent } from './module-switcher/module-switcher.component';
import { PinnedModulesComponent } from './pinned-modules/pinned-modules.component';

/**
 * TopNavigationComponent component.
 * Provides UI presentation and event handling for top navigation component.
 */
@Component({
  selector: 'app-top-navigation',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    MatTooltipModule,
    AppLogoComponent,
    TranslatePipe,
    IcNotificationComponent,
    IcSearchComponent,
    IcUserComponent,
    ModuleSwitcherComponent,
    MegaMenuComponent,
    PinnedModulesComponent,
    BreadcrumbShipmentNavComponent,
  ],
  templateUrl: './top-navigation.component.html',
  styleUrl: './top-navigation.component.scss',
})
export class TopNavigationComponent implements OnDestroy {
  /** Injected service for layout service. */
  readonly layoutService = inject(LayoutService);
  /** Injected service for navigation state. */
  readonly navigationState = inject(NavigationStateService);
  /** Injected service for pinned modules service. */
  readonly pinnedModulesService = inject(PinnedModulesService);
  /** Injected service for breadcrumb service. */
  private breadcrumbService = inject(BreadcrumbService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);
  /** Injected service for local storage service. */
  private localStorageService = inject(LocalStorageService);
  /** Injected service for utilities. */
  private utilities = inject(UtilitiesService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for role. */
  private role = inject(RoleService);
  /** Injected service for rto access service. */
  private rtoAccessService = inject(RtoAccessService);
  /** Property dialog ref. */
  private dialogRef: DynamicDialogRef | undefined | null;

  /** Property search shortcut. */
  searchShortcut = this.utilities.getPlatformShortcut('⌘K');

  /** Same breadcrumb source as the standard header. */
  breadcrumbItems = this.breadcrumbService.breadcrumbItems;

  /**
   * Detail/context crumbs only. Home + module/screen labels are omitted because
   * the module switcher already shows that context.
   */
  contextBreadcrumbs = computed(() => {
    const items = this.breadcrumbItems();
    const mod = this.navigationState.currentModule();
    const screen = this.navigationState.currentScreen();
    const url = this.router.url.split('?')[0];
    const onScreenRoot = screen?.route
      ? url === screen.route || url === screen.route + '/'
      : false;

    const skippedKeys = new Set<string>(['home']);
    if (mod?.translationKey) skippedKeys.add(mod.translationKey);
    if (mod?.name) skippedKeys.add(mod.name);
    // Module key variants used in breadcrumb trails (e.g. transit vs transitNcts)
    if (mod?.key) {
      skippedKeys.add(mod.key);
      skippedKeys.add(mod.key.replace(/-/g, ''));
    }
    if (screen?.translationKey) skippedKeys.add(screen.translationKey);
    if (screen?.name) skippedKeys.add(screen.name);
    if (screen?.key) skippedKeys.add(screen.key);

    // Common module label aliases that appear in breadcrumb trails
    if (mod?.key === 'transit') {
      skippedKeys.add('transit');
      skippedKeys.add('transitNcts');
    }
    if (mod?.key === 'settings') {
      skippedKeys.add('globalModules');
      skippedKeys.add('settings');
    }
    if (screen?.key === 'declaration-templates') {
      skippedKeys.add('declarationTemplates');
      skippedKeys.add('templates');
    }

    // List crumbs use import/export-prefixed labels that differ from screen keys
    if (screen?.key === 'declarations') {
      skippedKeys.add('importDeclarations');
      skippedKeys.add('exportDeclarations');
      skippedKeys.add('declarations');
    }
    if (screen?.key === 'shipments') {
      skippedKeys.add('importShipments');
      skippedKeys.add('exportShipments');
      skippedKeys.add('shipments');
    }

    return items.filter((item) => {
      if (skippedKeys.has(item.itemName)) {
        // On detail/edit routes, keep the screen list crumb as a navigable back link.
        if (
          !onScreenRoot &&
          screen?.route &&
          item.route === screen.route
        ) {
          return true;
        }
        return false;
      }
      // Skip crumbs whose route is exactly the module or screen list route (on list pages)
      if (item.route && item.route === mod?.route) {
        return false;
      }
      if (
        item.route &&
        item.route === screen?.route &&
        onScreenRoot
      ) {
        return false;
      }
      return true;
    });
  });

  /** Property is shipment details page. */
  isShipmentDetailsPage = computed(() => {
    this.breadcrumbItems();
    return /\/shipments\/details\//.test(this.router.url.split('?')[0]);
  });

  /**
   * Handles show shipment breadcrumb nav logic.
   * @param item - The item
   * @param index - The index
   */
  showShipmentBreadcrumbNav(item: Breadcrumb, index: number): boolean {
    if (!this.isShipmentDetailsPage() || !item.isDynamic) {
      return false;
    }

    const items = this.contextBreadcrumbs();
    return index === items.length - 1;
  }

  /**
   * Handles close current dialog logic.
   */
  private closeCurrentDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  /**
   * Handles on global search click logic.
   */
  onGlobalSearchClick(): void {
    this.closeCurrentDialog();
    this.dialogRef = this.dialogService.open(GlobalSearch, {
      width: '700px',
      height: 'auto',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'custom-header',
      style: {
        'border-radius': '10px',
        position: 'fixed',
        top: '60px',
        left: '50%',
        transform: 'translateX(-50%)',
      },
    });
  }

  /**
   * Handles on notification click logic.
   */
  onNotificationClick(): void {
    this.closeCurrentDialog();
    this.dialogRef = this.dialogService.open(NotificationsAndAlertsComponent, {
      data: { activeTab: 'notifications' },
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on alert click logic.
   */
  onAlertClick(): void {
    this.closeCurrentDialog();
    this.dialogRef = this.dialogService.open(NotificationsAndAlertsComponent, {
      data: { activeTab: 'alerts' },
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on recent downloads click logic.
   */
  onRecentDownloadsClick(): void {
    this.closeCurrentDialog();
    this.dialogRef = this.dialogService.open(RecentDownloadsPopupComponent, {
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on profile click logic.
   */
  onProfileClick(): void {
    this.closeCurrentDialog();
    this.dialogRef = this.dialogService.open(UserProfilePopupComponent, {
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });

    if (this.dialogRef) {
      this.dialogRef.onClose.subscribe((result) => {
        if (result?.action === 'logout') {
          this.localStorageService.signOut();
          this.router.navigate(['/auth/login']);
        } else if (result?.action === 'changePassword') {
          this.router.navigate(['/auth/change-password']);
        }
      });
    }
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy(): void {
    this.closeCurrentDialog();
  }

  /**
   * Handles context breadcrumb link click, verifying RTO access permissions.
   * @param item - Clicked breadcrumb item
   * @param event - Mouse click event
   */
  onBreadcrumbClick(item: Breadcrumb, event: MouseEvent): void {
    if (
      item.route &&
      !item.route.startsWith('/dashboard') &&
      (item.route.startsWith('/rto') || item.route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.rtoAccessService.openRtoNotConfiguredModal();
    }
  }
}
